export type DiffKind = 'context' | 'added' | 'removed';

export interface DiffLine {
  kind: DiffKind;
  text: string;
}

/**
 * Guard on the LCS table below, which is O(n x m) in both time and memory.
 *
 * Every config in the registry today is well under a hundred lines, so this
 * never fires in practice — it exists so that pasting something enormous into
 * the JSON tab degrades to a coarse diff instead of locking the tab up.
 */
const MAX_DIFF_LINES = 1000;

/**
 * Line diff between the saved value and the draft, for the review step.
 *
 * Classic LCS backtrack. Both sides are pretty-printed JSON produced by the same
 * serialiser, so key order and indentation are already normalised — a line
 * differs here only because its content differs, not because the formatting
 * drifted.
 */
export const diffLines = (before: string, after: string): DiffLine[] => {
  const a = before.split('\n');
  const b = after.split('\n');

  if (a.length > MAX_DIFF_LINES || b.length > MAX_DIFF_LINES) {
    return [
      ...a.map((text): DiffLine => ({ kind: 'removed', text })),
      ...b.map((text): DiffLine => ({ kind: 'added', text })),
    ];
  }

  const n = a.length;
  const m = b.length;

  // lcs[i][j] = length of the longest common subsequence of a[i:] and b[j:].
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));

  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;

  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ kind: 'context', text: a[i] });
      i += 1;
      j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      // Tie goes to the removal, so a changed line reads as "- old" then
      // "+ new" rather than the reverse.
      out.push({ kind: 'removed', text: a[i] });
      i += 1;
    } else {
      out.push({ kind: 'added', text: b[j] });
      j += 1;
    }
  }

  while (i < n) {
    out.push({ kind: 'removed', text: a[i] });
    i += 1;
  }

  while (j < m) {
    out.push({ kind: 'added', text: b[j] });
    j += 1;
  }

  return out;
};

export const countDiff = (lines: DiffLine[]) => ({
  added: lines.filter((line) => line.kind === 'added').length,
  removed: lines.filter((line) => line.kind === 'removed').length,
});
